import { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Assessment,
  LifeCyclePosition,
  ReportData,
  StrategyStability,
} from "types/assessmentTypes";
import { useUpdateAssessmentReportMutation } from "reduxFiles/api/assessmentApi";

interface UseAssessmentReportSyncProps {
  assessment: Assessment | undefined;
  isFetching: boolean;
  calculatedOhi: number;
  calculatedLifeCycleScore: number;
  calculatedLifeCyclePosition: LifeCyclePosition;
  calculatedStrategyStability: StrategyStability;
  calculatedStrategyOrder: number;
  scope: string;
}

export function useAssessmentReportSync({
  assessment,
  isFetching,
  calculatedOhi,
  calculatedLifeCycleScore,
  calculatedLifeCyclePosition,
  calculatedStrategyStability,
  calculatedStrategyOrder,
  scope,
}: UseAssessmentReportSyncProps) {
  const { id } = useParams<{ id: string }>();

  const [updateReport, { isLoading: isUpdating }] =
    useUpdateAssessmentReportMutation();

  const hasReportData = (): boolean => {
    if (!assessment?.report) return false;

    const report = assessment.report;
    return (
      report.ohi !== null &&
      report.ohi !== undefined &&
      report.lifeCycle?.score !== null &&
      report.lifeCycle?.score !== undefined &&
      report.lifeCycle?.position !== null &&
      report.lifeCycle?.position !== undefined &&
      report.strategy?.stability !== null &&
      report.strategy?.stability !== undefined &&
      report.strategy?.order !== null &&
      report.strategy?.order !== undefined
    );
  };

  const calculateReportData = (): ReportData | null => {
    if (!assessment) return null;

    return {
      ohi: calculatedOhi,
      lifeCycle: {
        score: calculatedLifeCycleScore,
        position: calculatedLifeCyclePosition,
      },
      strategy: {
        stability: calculatedStrategyStability,
        order: calculatedStrategyOrder,
      },
    };
  };

  const syncReport = async () => {
    if (scope !== "user") return;

    if (!id || !assessment || isFetching || isUpdating) return;

    if (hasReportData()) return;

    const reportData = calculateReportData();
    if (!reportData) return;

    try {
      await updateReport({
        id: Number(id),
        body: {
          ohi: reportData.ohi,
          lifeCycle: {
            score: Number(reportData.lifeCycle.score?.toFixed(2)),
            position: reportData.lifeCycle.position,
          },
          strategy: {
            stability: reportData.strategy.stability,
            order: reportData.strategy.order,
          },
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to sync assessment report:", error);
    }
  };

  useEffect(() => {
    if (!isFetching && assessment) {
      syncReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment, isFetching, scope]);

  return {
    isUpdating,
  };
}
